/**
 * src/db/locations.js — Sentavus Locations data layer
 *
 * The locations dictionary (metro, parks, schools, hospitals, complexes, ...)
 * plus the rich `complexes` entity. Mirrors the patterns in src/db/index.js:
 * Supabase when configured, localStorage fallback otherwise.
 *
 * Proximity lookup runs server-side via the nearby_locations / nearby_complexes
 * RPCs (PostGIS). The local fallback does the same with a Haversine scan so the
 * app still works without Supabase.
 *
 * Schema: locations-schema.sql   |   Seed: locations-seed.sql
 */
import { supabase, USE_SUPABASE } from './index.js'

const LS_LOCATIONS = 'sva_locations_v1'
const LS_COMPLEXES = 'sva_complexes_v1'
const LS_PQVOTES   = 'sva_pqvotes_v1'

function lsRead(key)         { try { return JSON.parse(localStorage.getItem(key) || '[]') } catch { return [] } }
function lsWrite(key, value) { localStorage.setItem(key, JSON.stringify(value)) }

// Haversine distance in metres (local-mode fallback for proximity search).
function haversine(lat1, lng1, lat2, lng2) {
  const R = 6371000, toRad = d => (d * Math.PI) / 180
  const dLat = toRad(lat2 - lat1), dLng = toRad(lng2 - lng1)
  const a = Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) ** 2
  return 2 * R * Math.asin(Math.sqrt(a))
}

// ─── Locations dictionary ─────────────────────────────────────────────────────
export async function getLocations({ type } = {}) {
  if (!USE_SUPABASE) {
    let rows = lsRead(LS_LOCATIONS)
    if (type) rows = rows.filter(r => r.type === type)
    return rows
  }
  let q = supabase.from('locations').select('*').order('type').order('name')
  if (type) q = q.eq('type', type)
  const { data, error } = await q
  if (error) throw error
  return data || []
}

export async function addLocation(record) {
  // record: { type, name, district?, lat, lng, meta?, source? }
  if (!USE_SUPABASE) {
    const row = { ...record, id: crypto.randomUUID(), meta: record.meta || {} }
    lsWrite(LS_LOCATIONS, [...lsRead(LS_LOCATIONS), row])
    return row
  }
  const { data, error } = await supabase.from('locations').insert(record).select().single()
  if (error) throw error
  return data
}

export async function updateLocation(id, updates) {
  if (!USE_SUPABASE) {
    const rows = lsRead(LS_LOCATIONS).map(r => r.id === id ? { ...r, ...updates } : r)
    lsWrite(LS_LOCATIONS, rows)
    return rows.find(r => r.id === id)
  }
  const { data, error } = await supabase.from('locations').update(updates).eq('id', id).select().single()
  if (error) throw error
  return data
}

export async function deleteLocation(id) {
  if (!USE_SUPABASE) {
    lsWrite(LS_LOCATIONS, lsRead(LS_LOCATIONS).filter(r => r.id !== id))
    return
  }
  const { error } = await supabase.from('locations').delete().eq('id', id)
  if (error) throw error
}

/**
 * Everything within `radiusM` of a point, nearest first.
 * @param {number} lat @param {number} lng
 * @param {number} radiusM  default 1000
 * @param {string[]|null} types  e.g. ['metro','school']; null = all types
 * Returns rows with an added `distance_m`.
 */
export async function nearbyLocations(lat, lng, radiusM = 1000, types = null) {
  if (!USE_SUPABASE) {
    return lsRead(LS_LOCATIONS)
      .filter(r => !types || types.includes(r.type))
      .map(r => ({ ...r, distance_m: haversine(lat, lng, r.lat, r.lng) }))
      .filter(r => r.distance_m <= radiusM)
      .sort((a, b) => a.distance_m - b.distance_m)
  }
  const { data, error } = await supabase.rpc('nearby_locations', {
    p_lat: lat, p_lng: lng, p_radius_m: radiusM, p_types: types,
  })
  if (error) throw error
  return data || []
}

// ─── Complexes (rich entity for the dedicated page) ───────────────────────────
export async function getComplexes() {
  if (!USE_SUPABASE) return lsRead(LS_COMPLEXES)
  const { data, error } = await supabase.from('complexes').select('*').order('name')
  if (error) throw error
  return data || []
}

export async function getComplex(id) {
  if (!USE_SUPABASE) return lsRead(LS_COMPLEXES).find(c => c.id === id) || null
  const { data, error } = await supabase.from('complexes').select('*').eq('id', id).single()
  if (error) throw error
  return data
}

export async function updateComplex(id, updates) {
  if (!USE_SUPABASE) {
    const rows = lsRead(LS_COMPLEXES).map(c => c.id === id ? { ...c, ...updates } : c)
    lsWrite(LS_COMPLEXES, rows)
    return rows.find(c => c.id === id)
  }
  const { data, error } = await supabase.from('complexes').update(updates).eq('id', id).select().single()
  if (error) throw error
  return data
}

/** Nearest complexes to a point — use a small radius (~150m) to tag a listing. */
export async function nearbyComplexes(lat, lng, radiusM = 150) {
  if (!USE_SUPABASE) {
    const locs = lsRead(LS_LOCATIONS).filter(r => r.type === 'complex')
    const cplx = lsRead(LS_COMPLEXES)
    return locs
      .map(l => ({ l, distance_m: haversine(lat, lng, l.lat, l.lng) }))
      .filter(x => x.distance_m <= radiusM)
      .sort((a, b) => a.distance_m - b.distance_m)
      .map(({ l, distance_m }) => {
        const c = cplx.find(x => x.location_id === l.id) || {}
        return { complex_id: c.id, name: l.name, district: l.district,
          developer: c.developer, status: c.status, lat: l.lat, lng: l.lng, distance_m }
      })
  }
  const { data, error } = await supabase.rpc('nearby_complexes', {
    p_lat: lat, p_lng: lng, p_radius_m: radiusM,
  })
  if (error) throw error
  return data || []
}

// ─── Property question votes (generic QuestionBox pattern, 2026-07-16) ─────────
// Append-only: one row per answer, LATEST row per agent wins, verdict is computed
// by the consumer per kind (see property-questions-schema.sql). First consumer:
// kind='complex_membership' in the property modal's LOCATION card.

/** All votes for one property+kind, newest first (consumer picks latest-per-agent). */
export async function getPropertyQuestionVotes(propertyId, kind) {
  if (!USE_SUPABASE) {
    return lsRead(LS_PQVOTES)
      .filter(r => r.property_id === propertyId && r.kind === kind)
      .sort((a, b) => (a.created_at < b.created_at ? 1 : -1))
  }
  const { data, error } = await supabase.from('property_question_votes').select('*')
    .eq('property_id', propertyId).eq('kind', kind)
    .order('created_at', { ascending: false })
  if (error) throw error
  return data || []
}

/**
 * Record an answer. `metadata` must snapshot the evidence the voter saw
 * (e.g. { distance_at_vote_m: 42 }) so votes cast against a since-moved pin
 * can be invalidated later. answer: 'yes' | 'no' | 'skip'.
 */
export async function castPropertyQuestionVote({ kind, propertyId, subjectId, agentId, answer, metadata = {} }) {
  const record = {
    kind, property_id: propertyId, subject_id: String(subjectId),
    agent_id: agentId, answer, metadata,
  }
  if (!USE_SUPABASE) {
    const row = { ...record, id: crypto.randomUUID(), created_at: new Date().toISOString() }
    lsWrite(LS_PQVOTES, [...lsRead(LS_PQVOTES), row])
    return row
  }
  const { data, error } = await supabase.from('property_question_votes').insert(record).select().single()
  if (error) throw error
  return data
}
